import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import Login from "./Login";
import ERP from "./ERP";
import { supabase } from "./supabaseClient";
import GlobalStyle from "./GlobalStyle";
import Navbar from "./Navbar";
import Footer from "./Footer";
import Scene3D from "./Scene3D";
import { Reveal, Burst, Counter, TiltCard, WhatsAppDemo, PaymentDemo, useSEO } from "./UIKit";
import WhyPage from "./screen/WhyPage";
import ProductPage from "./screen/ProductPage";
import MarketPage from "./screen/MarketPage";
import ApproachPage from "./screen/ApproachPage";
import AboutPage from "./screen/AboutPage";

/* ============================================================
   HOME — the original single-page site, now with "explore" buttons
   into the dedicated pages, and a three.js 3D hero.
   ============================================================ */
function HomeSite({ onAuthClick }) {
  const [formState, setFormState] = useState({ name: "", society: "", city: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useSEO({
    title: "Bellboy — Security & Society Management App for Housing Societies",
    description:
      "Bellboy is a visitor security and society management app for Indian housing societies — AI gate security, WhatsApp-based admin, and real-time payment tracking.",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    const { error } = await supabase.from("walkthrough_requests").insert({
      name: formState.name.trim(),
      society: formState.society.trim(),
      city: formState.city.trim(),
      source: "website_cta",
      status: "new",
    });
    if (error) {
      setSubmitError("We couldn't save that request. Please try again.");
      return;
    }
    setSubmitted(true);
  };

  const pillars = [
    { tag: "SECURITY", title: "AI-based visitor & vehicle entry", body: "Number-plate and facial recognition move cabs and deliveries through the gate automatically — no manual plate entry, no one-by-one approvals." },
    { tag: "PRIVACY", title: "Data hosted on your society's own server", body: "Visitor logs and financial records stay with your society, not on a shared corporate cloud." },
    { tag: "ADMIN", title: "WhatsApp-based admin", body: "Residents and committee members handle bookings, complaints, and notices directly over WhatsApp, in text or voice." },
    { tag: "MAINTENANCE", title: "Predictive maintenance alerts", body: "IoT sensors on lifts and pumps flag developing faults before they turn into breakdowns and emergency repair bills." },
    { tag: "HARDWARE", title: "Hardware-as-a-Service", body: "Cameras and sensors are included with your subscription instead of a large upfront purchase." },
  ];

  const problems = [
    "Guards note down every visitor and delivery by hand, entry after entry.",
    "Residents have to open an app and approve every visitor request one at a time.",
    "Committees chase residents individually for pending maintenance payments.",
    "Lift and pump breakdowns get flagged only after they've already failed.",
  ];

  const gtm = [
    { title: "Partner with local installers", body: "CCTV and intercom vendors already inside these societies bundle Bellboy in with hardware they're already selling." },
    { title: "Design for guards first", body: "Voice-to-text logging, regional-language interfaces, and simple icons — built for the person using the app dozens of times a day." },
    { title: "Get built into new townships", body: "Direct tie-ups with regional developers mean Bellboy is pre-installed before residents even move in." },
    { title: "Add a local marketplace later", body: "Grocers, water-tanker operators, and domestic-help services listed directly for residents to book." },
  ];

  return (
    <div className="bb-root">
      <GlobalStyle />
      <Navbar onAuthClick={onAuthClick} />

      {/* HERO */}
      <header id="top" className="hero">
        <div className="hero-copy">
          <Reveal className="eyebrow">Society management app for Indian RWAs</Reveal>
          <Reveal as="h1" delay={80} className="hero-title">
            Security, payments and admin for your housing society — in one app.
          </Reveal>
          <Reveal delay={160} className="hero-sub">
            Bellboy automates gate security, tracks maintenance payments, and handles day-to-day admin over
            WhatsApp — built for Indian housing societies and RWAs.
          </Reveal>
          <Reveal delay={240} className="hero-actions">
            <button className="btn btn-primary" onClick={() => onAuthClick("register")}>Bring Bellboy to your society</button>
            <a href="#pillars" className="btn btn-ghost">See how it works</a>
          </Reveal>
        </div>
        <Reveal delay={120} className="hero-visual">
          <div className="gate-stage">
            <Scene3D mode="gate" />
          </div>
        </Reveal>
      </header>

      {/* PROBLEM */}
      <section id="problem" className="section section-light">
        <Reveal as="h2" className="section-title">Running a society still takes too much manual work.</Reveal>
        <Reveal as="p" delay={80} className="section-lede">
          Visitor logs, payment follow-ups, and complaint tracking eat up hours every week for committees and
          guards alike.
        </Reveal>
        <div className="problem-grid">
          {problems.map((p, i) => (
            <Reveal key={i} delay={i * 90} className="problem-row">
              <span className="problem-index">{String(i + 1).padStart(2, "0")}</span>
              <span>{p}</span>
            </Reveal>
          ))}
        </div>
        <Reveal delay={120}>
          <Link to="/why" className="btn btn-ghost" style={{ marginTop: 32 }}>Read the full breakdown →</Link>
        </Reveal>
      </section>

      {/* PILLARS */}
      <section id="pillars" className="section section-indigo">
        <Reveal as="h2" className="section-title on-dark">Everything your society needs, in one app.</Reveal>
        <Reveal as="p" delay={80} className="section-lede on-dark">
          Visitor management, accounting, and helpdesk — plus the automation that saves your guards and
          committee real time.
        </Reveal>
        <Reveal delay={40} className="pillar-jumplist" aria-label="Jump to a product pillar">
          {pillars.map((p) => (
            <a key={p.tag} href={`#pillar-${p.tag.toLowerCase()}`}>{p.title}</a>
          ))}
        </Reveal>
        <div className="pillar-grid">
          {pillars.map((p, i) => (
            <Reveal key={p.title} id={`pillar-${p.tag.toLowerCase()}`} delay={i * 80}>
              <TiltCard className="pillar-card">
                <span className="pillar-tag">{p.tag}</span>
                <h3>{p.title}</h3>
                <p>{p.body}</p>
              </TiltCard>
            </Reveal>
          ))}
        </div>

        <div id="live-demo-chat" className="demo-row">
          <Reveal className="demo-copy">
            <span className="pillar-tag">LIVE DEMO</span>
            <h3>Bookings and complaints, handled over WhatsApp.</h3>
            <p>
              A resident or committee member sends a message the way they already message anyone else — and it
              gets handled. No new app habit to build.
            </p>
          </Reveal>
          <Reveal delay={100}>
            <WhatsAppDemo />
          </Reveal>
        </div>

        <div id="live-demo-payments" className="demo-row demo-row-reverse">
          <Reveal className="demo-copy">
            <span className="pillar-tag">LIVE DEMO</span>
            <h3>Payments, tracked the moment they land.</h3>
            <p>
              The committee sees collections update in real time — who's paid, who hasn't — without chasing
              anyone for a screenshot.
            </p>
          </Reveal>
          <Reveal delay={100}>
            <PaymentDemo />
          </Reveal>
        </div>

        <Reveal delay={140}>
          <Link to="/product" className="btn btn-primary" style={{ marginTop: 44 }}>Explore the full product →</Link>
        </Reveal>
      </section>

      {/* MARKET */}
      <section id="market" className="section section-light">
        <Reveal as="h2" className="section-title">A large, fast-growing market.</Reveal>
        <Reveal as="p" delay={80} className="section-lede">
          Housing societies across India are adopting management apps quickly — and most of the country is
          still untapped.
        </Reveal>
        <div className="stat-grid">
          <Reveal className="stat-card">
            <div className="stat-num">$<Counter to={5.8} decimals={1} suffix="B" /></div>
            <div className="stat-label">projected global market size by 2034, up from $2.1B in 2025</div>
          </Reveal>
          <Reveal delay={90} className="stat-card">
            <div className="stat-num"><Counter to={11.9} decimals={1} suffix="%" /></div>
            <div className="stat-label">projected CAGR through 2034</div>
          </Reveal>
          <Reveal delay={180} className="stat-card">
            <div className="stat-num"><Counter to={38} decimals={0} suffix="%" /></div>
            <div className="stat-label">of global category revenue comes from Asia-Pacific alone</div>
          </Reveal>
          <Reveal delay={270} className="stat-card">
            <div className="stat-num">$<Counter to={0.15} decimals={2} suffix="B" /></div>
            <div className="stat-label">India's own market size in 2026 — concentrated in three metros</div>
          </Reveal>
        </div>
        <Reveal delay={120} className="market-note">
          Tier-2 and Tier-3 cities are where new townships are being built fastest — and where today's apps
          have barely arrived.
        </Reveal>
        <Reveal delay={160}>
          <Link to="/market" className="btn btn-ghost" style={{ marginTop: 28 }}>See the full market deep-dive →</Link>
        </Reveal>
      </section>

      {/* GTM */}
      <section id="gtm" className="section section-sky">
        <Reveal as="h2" className="section-title">How we reach new societies.</Reveal>
        <Reveal as="p" delay={80} className="section-lede">
          Distribution in Tier-2/3 India runs on local trust and physical presence, not app-store ads.
        </Reveal>
        <div className="gtm-grid">
          {gtm.map((g, i) => (
            <Reveal key={g.title} delay={i * 100}>
              <TiltCard className="gtm-card">
                <h3>{g.title}</h3>
                <p>{g.body}</p>
              </TiltCard>
            </Reveal>
          ))}
        </div>
        <Reveal delay={140}>
          <Link to="/approach" className="btn btn-primary" style={{ marginTop: 36 }}>See our full approach →</Link>
        </Reveal>
      </section>

      {/* ABOUT TEASER */}
      <section id="about-teaser" className="section section-indigo">
        <Reveal as="h2" className="section-title on-dark">Built by one founder.</Reveal>
        <Reveal as="p" delay={80} className="section-lede on-dark">
          Bellboy is a pre-seed startup founded by Tejeshwar Mishra, building security and society-management
          software for India's housing societies.
        </Reveal>
        <Reveal delay={140}>
          <Link to="/about" className="btn btn-ghost" style={{ marginTop: 28 }}>Meet the team →</Link>
        </Reveal>
      </section>

      {/* CTA */}
      <section id="cta" className="section section-cta">
        <Reveal as="h2" className="section-title">Get Bellboy for your society.</Reveal>
        <Reveal as="p" delay={80} className="section-lede">
          Free security core. No card required. Your installer probably already knows us.
        </Reveal>
        <Reveal delay={160} className="cta-form-wrap">
          {submitted ? (
            <div className="cta-success">Thanks — someone from Bellboy will reach out shortly.</div>
          ) : (
            <form className="cta-form" onSubmit={handleSubmit}>
              <input
                required
                placeholder="Your name"
                value={formState.name}
                onChange={(e) => setFormState({ ...formState, name: e.target.value })}
              />
              <input
                required
                placeholder="Society name"
                value={formState.society}
                onChange={(e) => setFormState({ ...formState, society: e.target.value })}
              />
              <input
                required
                placeholder="City"
                value={formState.city}
                onChange={(e) => setFormState({ ...formState, city: e.target.value })}
              />
              <button type="submit" className="btn btn-primary">Request a walkthrough</button>
              {submitError && <div role="alert" style={{ marginTop: 10, color: "#fff", fontWeight: 700 }}>{submitError}</div>}
            </form>
          )}
        </Reveal>
      </section>

      {/* DOWNLOAD APP SECTION */}
      <section id="download" className="app-download-section">
        <div className="app-download-container">
          <div className="app-download-content">
            <Burst className="burst-small">NEW!</Burst>
            <div>
              <h2>Download the App</h2>
              <p>Get the Android app directly on your mobile device for seamless access.</p>
            </div>
          </div>
          <a href="https://expo.dev/artifacts/eas/QZgMHMUERg4GUHKbYYBBfO951o72-cckHpQQwNbgOxg.apk" download="Bellboy.apk" className="btn btn-primary app-download-btn">
            Download APK
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}

/* ============================================================
   MAIN APP — auth/ERP state machine, router for the marketing site
   ============================================================ */
export default function App() {
  const [view, setView] = useState("site"); // "site" | "auth" | "erp"
  const [authMode, setAuthMode] = useState("login");
  const [session, setSession] = useState(null);

  useSEO({
    title:
      view === "auth" ? "Log in or register — Bellboy" : view === "erp" ? "Bellboy ERP Dashboard" : "Bellboy",
    description:
      view === "auth"
        ? "Sign in to manage your housing society with Bellboy — visitor security, billing, complaints, and accounting in one dashboard."
        : "Bellboy is a security and society management app for Indian housing societies — visitor management, WhatsApp admin, and real-time payment tracking.",
    indexable: view === "site",
  });

  useEffect(() => {
    let alive = true;
    const restore = async () => {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      if (!alive || !authSession?.user) return;
      const { data: memberships } = await supabase.from("society_members").select("role, society_id, societies(name, plan)").eq("user_id", authSession.user.id).eq("status", "active").in("role", ["admin", "committee"]);
      const membership = memberships?.[0];
      if (membership && alive) {
        const { data: profile } = await supabase.from("profiles").select("full_name, email").eq("id", authSession.user.id).maybeSingle();
        setSession({ userId: authSession.user.id, role: membership.role, societyId: membership.society_id, societyName: membership.societies?.name, adminName: profile?.full_name || "Admin", email: profile?.email || authSession.user.email, plan: membership.societies?.plan || "399" });
        setView("erp");
      }
    };
    restore();
    const { data: listener } = supabase.auth.onAuthStateChange((_event, next) => { if (!next && alive) { setSession(null); setView("site"); } });
    return () => { alive = false; listener.subscription.unsubscribe(); };
  }, []);

  const openAuth = (mode) => {
    setAuthMode(mode);
    setView("auth");
  };

  if (view === "erp" && session) {
    return (
      <ERP
        session={session}
        onLogout={async () => {
          await supabase.auth.signOut();
          setSession(null);
          setView("site");
        }}
      />
    );
  }

  if (view === "auth") {
    return (
      <>
        <GlobalStyle />
        <Login
          initialMode={authMode}
          onCancel={() => setView("site")}
          onAuthenticated={(s) => {
            setSession(s);
            setView("erp");
          }}
        />
      </>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomeSite onAuthClick={openAuth} />} />
        <Route path="/why" element={<WhyPage onAuthClick={openAuth} />} />
        <Route path="/product" element={<ProductPage onAuthClick={openAuth} />} />
        <Route path="/market" element={<MarketPage onAuthClick={openAuth} />} />
        <Route path="/approach" element={<ApproachPage onAuthClick={openAuth} />} />
        <Route path="/about" element={<AboutPage onAuthClick={openAuth} />} />
        <Route path="*" element={<HomeSite onAuthClick={openAuth} />} />
      </Routes>
    </BrowserRouter>
  );
}
